import { supabase } from "../lib/supabase";

/**
 * Service for managing template categories and their assignments.
 *
 * Category types:
 * - base: Always applied (e.g., "All Projects")
 * - deliverable: Surface Design, Linework Only
 * - project_type: Commercial, Subdivision, Pond, etc.
 * - add_on: Has Pond, Elevated Utilities, Advanced Utility Model
 * - file_format: Trimble, Topcon, Leica
 * - unit_system: International Feet (managed by DB trigger auto_add_international_feet, NOT by frontend)
 */

export class TemplateCategoryService {
  /**
   * Transform database category to frontend format
   */
  static transformCategory(category) {
    return {
      id: category.id,
      name: category.name,
      displayName: category.display_name,
      categoryType: category.category_type,
      description: category.description,
      sortOrder: category.sort_order,
      isActive: category.is_active
    };
  }

  /**
   * Get all active categories
   */
  static async getCategories() {
    try {
      const { data, error } = await supabase
        .from('template_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching categories:', error);
        throw new Error(`Failed to fetch categories: ${error.message}`);
      }

      return data?.map(this.transformCategory) || [];
    } catch (error) {
      console.error('Error in getCategories:', error);
      throw error;
    }
  }

  /**
   * Get categories filtered by type
   * @param {string} type - Category type: 'base', 'deliverable', 'project_type', 'add_on', 'file_format', 'unit_system'
   */
  static async getCategoriesByType(type) {
    try {
      const { data, error } = await supabase
        .from('template_categories')
        .select('*')
        .eq('category_type', type)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching categories by type:', error);
        throw new Error(`Failed to fetch categories: ${error.message}`);
      }

      return data?.map(this.transformCategory) || [];
    } catch (error) {
      console.error('Error in getCategoriesByType:', error);
      throw error;
    }
  }

  /**
   * Get categories grouped by type (useful for UI)
   */
  static async getCategoriesGrouped() {
    try {
      const categories = await this.getCategories();

      return {
        base: categories.filter(c => c.categoryType === 'base'),
        deliverable: categories.filter(c => c.categoryType === 'deliverable'),
        projectType: categories.filter(c => c.categoryType === 'project_type'),
        addOn: categories.filter(c => c.categoryType === 'add_on'),
        fileFormat: categories.filter(c => c.categoryType === 'file_format'),
        unitSystem: categories.filter(c => c.categoryType === 'unit_system')
      };
    } catch (error) {
      console.error('Error in getCategoriesGrouped:', error);
      throw error;
    }
  }

  /**
   * Get category IDs assigned to a specific template
   * @param {number} templateId - The subitem_templates.id
   * @returns {number[]} Array of category IDs
   */
  static async getTemplateCategories(templateId) {
    try {
      const { data, error } = await supabase
        .from('template_category_assignments')
        .select('category_id')
        .eq('template_id', templateId);

      if (error) {
        console.error('Error fetching template categories:', error);
        throw new Error(`Failed to fetch template categories: ${error.message}`);
      }

      return data?.map(row => row.category_id) || [];
    } catch (error) {
      console.error('Error in getTemplateCategories:', error);
      throw error;
    }
  }

  /**
   * Get full category details for a template
   * @param {number} templateId - The subitem_templates.id
   */
  static async getTemplateCategoriesWithDetails(templateId) {
    try {
      const { data, error } = await supabase
        .from('template_category_assignments')
        .select(`
          category_id,
          template_categories (
            id,
            name,
            display_name,
            category_type,
            description,
            sort_order,
            is_active
          )
        `)
        .eq('template_id', templateId);

      if (error) {
        console.error('Error fetching template categories with details:', error);
        throw new Error(`Failed to fetch template categories: ${error.message}`);
      }

      return data?.map(row => this.transformCategory(row.template_categories)) || [];
    } catch (error) {
      console.error('Error in getTemplateCategoriesWithDetails:', error);
      throw error;
    }
  }

  /**
   * Update category assignments for a template (replace all)
   * @param {number} templateId - The subitem_templates.id
   * @param {number[]} categoryIds - Array of category IDs to assign
   */
  static async updateTemplateCategories(templateId, categoryIds) {
    try {
      // Delete existing assignments
      const { error: deleteError } = await supabase
        .from('template_category_assignments')
        .delete()
        .eq('template_id', templateId);

      if (deleteError) {
        console.error('Error deleting template categories:', deleteError);
        throw new Error(`Failed to update template categories: ${deleteError.message}`);
      }

      // Insert new assignments (if any)
      if (categoryIds && categoryIds.length > 0) {
        const assignments = categoryIds.map(categoryId => ({
          template_id: templateId,
          category_id: categoryId
        }));

        const { error: insertError } = await supabase
          .from('template_category_assignments')
          .insert(assignments);

        if (insertError) {
          console.error('Error inserting template categories:', insertError);
          throw new Error(`Failed to update template categories: ${insertError.message}`);
        }
      }

      return true;
    } catch (error) {
      console.error('Error in updateTemplateCategories:', error);
      throw error;
    }
  }

  /**
   * Get all templates that have a specific category
   * @param {number} categoryId - The template_categories.id
   */
  static async getTemplatesByCategory(categoryId) {
    try {
      const { data, error } = await supabase
        .from('template_category_assignments')
        .select(`
          template_id,
          subitem_templates (
            id,
            name,
            phase,
            order_number,
            revision_type
          )
        `)
        .eq('category_id', categoryId);

      if (error) {
        console.error('Error fetching templates by category:', error);
        throw new Error(`Failed to fetch templates: ${error.message}`);
      }

      return data?.map(row => ({
        id: row.subitem_templates.id,
        name: row.subitem_templates.name,
        phase: row.subitem_templates.phase,
        order: row.subitem_templates.order_number,
        revisionType: row.subitem_templates.revision_type
      })) || [];
    } catch (error) {
      console.error('Error in getTemplatesByCategory:', error);
      throw error;
    }
  }

  /**
   * Get all templates with their categories in a single query
   * Returns templates with categoryIds array
   */
  static async getAllTemplatesWithCategories() {
    try {
      const { data, error } = await supabase
        .from('subitem_templates')
        .select(`
          id,
          name,
          phase,
          order_number,
          assignee,
          revision_type,
          template_filters,
          created_at,
          template_category_assignments (
            category_id
          )
        `)
        .order('order_number', { ascending: true });

      if (error) {
        console.error('Error fetching templates with categories:', error);
        throw new Error(`Failed to fetch templates: ${error.message}`);
      }

      return data?.map(template => ({
        id: template.id,
        name: template.name,
        phase: template.phase,
        order: template.order_number,
        revisionVersion: template.assignee,
        revisionType: template.revision_type,
        templateFilters: template.template_filters, // Keep for backwards compatibility
        categoryIds: template.template_category_assignments?.map(a => a.category_id) || [],
        createdAt: template.created_at
      })) || [];
    } catch (error) {
      console.error('Error in getAllTemplatesWithCategories:', error);
      throw error;
    }
  }

  // ============================================================
  // PROJECT CATEGORY METHODS
  // ============================================================

  /**
   * Get category IDs assigned to a specific project
   * @param {number} projectId - The projects.id
   * @returns {number[]} Array of category IDs
   */
  static async getProjectCategories(projectId) {
    try {
      const { data, error } = await supabase
        .from('project_categories')
        .select('category_id')
        .eq('project_id', projectId);

      if (error) {
        console.error('Error fetching project categories:', error);
        throw new Error(`Failed to fetch project categories: ${error.message}`);
      }

      return data?.map(row => row.category_id) || [];
    } catch (error) {
      console.error('Error in getProjectCategories:', error);
      throw error;
    }
  }

  /**
   * Get full category details for a project
   * @param {number} projectId - The projects.id
   */
  static async getProjectCategoriesWithDetails(projectId) {
    try {
      const { data, error } = await supabase
        .from('project_categories')
        .select(`
          category_id,
          template_categories (
            id,
            name,
            display_name,
            category_type,
            description,
            sort_order,
            is_active
          )
        `)
        .eq('project_id', projectId);

      if (error) {
        console.error('Error fetching project categories with details:', error);
        throw new Error(`Failed to fetch project categories: ${error.message}`);
      }

      return data?.map(row => this.transformCategory(row.template_categories)) || [];
    } catch (error) {
      console.error('Error in getProjectCategoriesWithDetails:', error);
      throw error;
    }
  }

  /**
   * Update category assignments for a project (replace all modal-owned types)
   *
   * Runs as one transaction in the DB (`set_project_categories`), which also
   * owns the list of modal-managed category types — trigger-managed ones
   * (unit_system / International Feet) are never touched. Idempotent: re-saving
   * the same selection is a no-op, so a retry can't hit a duplicate-key error.
   * @param {number} projectId - The projects.id
   * @param {number[]} categoryIds - Array of category IDs to assign
   */
  static async updateProjectCategories(projectId, categoryIds) {
    const { error } = await supabase.rpc('set_project_categories', {
      p_project_id: projectId,
      p_category_ids: categoryIds || []
    });

    if (error) {
      console.error('Error in updateProjectCategories:', error);
      // 42501 = the session dropped to anon (e.g. a failed token refresh)
      if (error.code === '42501') {
        throw new Error('Failed to update project categories: your sign-in expired — reload the page and try again.');
      }
      throw new Error(`Failed to update project categories: ${error.message}`);
    }

    return true;
  }

  /**
   * Add a single category to a project (without removing others)
   * @param {number} projectId - The projects.id
   * @param {number} categoryId - The category ID to add
   */
  static async addProjectCategory(projectId, categoryId) {
    try {
      const { error } = await supabase
        .from('project_categories')
        .upsert(
          { project_id: projectId, category_id: categoryId },
          { onConflict: 'project_id,category_id' }
        );

      if (error) {
        console.error('Error adding project category:', error);
        throw new Error(`Failed to add project category: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error in addProjectCategory:', error);
      throw error;
    }
  }

  /**
   * Remove a single category from a project
   * @param {number} projectId - The projects.id
   * @param {number} categoryId - The category ID to remove
   */
  static async removeProjectCategory(projectId, categoryId) {
    try {
      const { error } = await supabase
        .from('project_categories')
        .delete()
        .eq('project_id', projectId)
        .eq('category_id', categoryId);

      if (error) {
        console.error('Error removing project category:', error);
        throw new Error(`Failed to remove project category: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error in removeProjectCategory:', error);
      throw error;
    }
  }
}
